// src/hooks/usePermissions.js
// Hook centralizado para checar permisos del usuario actual.
// Combina useAuth + useOrganization para tomar en cuenta la impersonation
// del platform admin.
//
// Uso:
//   const { isAdmin, isHeadCoach, isCoach, canManageTorneos } = usePermissions();
//
// IMPORTANTE: NO reimplementar logica de permisos en componentes.
// Siempre usar este hook para mantener consistencia.

import { useAuth } from './useAuth'
import { useOrganization } from '../contexts/OrganizationContext'

export const usePermissions = () => {
  const { isPlatformAdmin } = useAuth()
  const { isOrgOverridden, rolEnOrgActiva } = useOrganization()

  // El rol sale de la MEMBRESIA en la org activa, no de `staff.rol`.
  //
  // `staff.rol` es un cache de la membresia PRINCIPAL: para quien pertenece a dos orgs
  // contesta siempre por una de ellas, y en la otra miente. Fallaba en las dos
  // direcciones -- un admin de Raiders con ficha `coach` no veia sus programas; un admin
  // de otra liga escribia donde solo era coach. La base dejo de preguntarle en 20260909
  // (organizacion) y 20260910 (grupo); esto es la mitad del cliente.
  //
  // Nunca se usa `user_metadata`: es editable por el propio usuario.
  const role = rolEnOrgActiva

  // Platform admin SIN override: solo modo plataforma
  const isPlatformOnlyView = isPlatformAdmin && !isOrgOverridden

  // Platform admin CON override: se trata como admin de esa org
  const isImpersonatingOrg = isPlatformAdmin && isOrgOverridden

  // Roles base
  const isAdmin = role === 'admin' || isImpersonatingOrg
  const isHeadCoach = role === 'head_coach'
  const isCoach = role === 'coach'
  const isManager = role === 'manager'
  const isAssistant = role === 'assistant'

  // Permisos derivados
  const canManagePrograms = isAdmin || isHeadCoach
  // Alcance de ORGANIZACION: crear torneos, ver invitaciones, catalogos. NO decide
  // sobre un grupo concreto -- para eso esta `puedeCapturarJuegos` en
  // lib/permisosJuegos, que espeja la politica de la base y pregunta por el grupo.
  const canManageTorneos = isAdmin || isHeadCoach
  const canManageStaff = isAdmin
  const canTakeAttendance = isAdmin || isHeadCoach || isCoach || isManager || isAssistant

  return {
    role,
    isPlatformAdmin,
    isPlatformOnlyView,
    isImpersonatingOrg,
    isAdmin,
    isHeadCoach,
    isCoach,
    isManager,
    isAssistant,
    canManagePrograms,
    canManageTorneos,
    canManageStaff,
    canTakeAttendance,
  }
}
